import { useLiveQuery } from 'dexie-react-hooks';
import { db, nowIso, putAndQueue } from '@/lib/db';
import { PROFILE_ID } from '@/features/profile/useProfile';

/**
 * Versión del texto de consentimiento. Si cambia lo que se explica al usuario,
 * se sube el número y se vuelve a pedir: un consentimiento del art. 9 RGPD es
 * para un tratamiento concreto, no un cheque en blanco.
 */
export const CONSENT_VERSION = 1;

const KEY = 'consent:salud';

export interface ConsentRecord {
  version: number;
  acceptedAt: string;
}

export type ConsentState = 'cargando' | 'pendiente' | 'aceptado';

/**
 * El consentimiento se guarda en DOS sitios a propósito:
 *
 *  - en `meta`, local, porque la puerta se pinta antes de que exista perfil;
 *  - en el PERFIL, que sí se sincroniza, porque es la base legal del
 *    tratamiento: si viviera solo en el navegador donde se dio, borrar los
 *    datos del sitio lo haría desaparecer aunque los datos de salud sigan en
 *    el servidor, y el segundo dispositivo volvería a pedirlo sin motivo.
 */
export function useConsent(): ConsentState {
  // `?? null` es imprescindible: useLiveQuery devuelve undefined mientras
  // consulta, y `meta.get` devuelve undefined cuando no hay registro. Sin
  // distinguirlos, "aún no ha aceptado" se confunde con "aún cargando" y la
  // app se queda en la pantalla de espera para siempre.
  const state = useLiveQuery(async () => {
    const local = ((await db.meta.get(KEY))?.value as ConsentRecord | undefined) ?? null;
    const profile = (await db.profile.get(PROFILE_ID)) ?? null;
    return { local, synced: profile?.healthConsent ?? null };
  }, []);

  if (state === undefined) return 'cargando';
  const accepted =
    state.local?.version === CONSENT_VERSION || state.synced?.version === CONSENT_VERSION;
  return accepted ? 'aceptado' : 'pendiente';
}

export async function acceptConsent(): Promise<void> {
  const record: ConsentRecord = { version: CONSENT_VERSION, acceptedAt: nowIso() };
  await db.meta.put({ key: KEY, value: record });
  await writeToProfile(record);
}

export async function revokeConsent(): Promise<void> {
  await db.meta.delete(KEY);
  await writeToProfile(null);
}

/**
 * Lo anota en el perfil si ya existe. En el primer arranque todavía no hay
 * perfil: lo escribe el onboarding al crearlo, leyendo el registro local.
 */
async function writeToProfile(record: ConsentRecord | null): Promise<void> {
  const profile = await db.profile.get(PROFILE_ID);
  if (!profile) return;
  await putAndQueue('profile', PROFILE_ID, {
    ...profile,
    healthConsent: record,
    updatedAt: nowIso(),
  });
}

/** El consentimiento local, para guardarlo con el perfil que se está creando. */
export async function currentConsent(): Promise<ConsentRecord | null> {
  return ((await db.meta.get(KEY))?.value as ConsentRecord | undefined) ?? null;
}
